import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, GuardResult, MaybeAsync, Router } from '@angular/router';
import { AuthenticationDto } from '@shared/dto/auth/Authentication.dto';
import { startup } from '@shared/rxjs-operators';
import { catchError, first, map, Observable, of, switchMap, tap, throwError, timer } from 'rxjs';
import { RedirectDataDto } from '../../.api-client/models/redirect-data-dto';
import { AuthService } from '../services/auth.service';
import { ErrorPageReasons } from '../shared/constants';
import { getSessionStorageInteger, resetSessionStorageInteger, setSessionStorageInteger } from '../shared/utils';

const SSO_INIT_COUNTER_SESSION_STORAGE_COUNTER_KEY = 'sso-init-counter';
const MAX_SSO_INIT_TRIES = 3;
const SSO_PARAMETERS = ['code', 'client_id', 'state', 'session_state', 'iss'];

export class SsoRedirectError {
    constructor(public readonly logoutUrl: string) {}
}

const cleanSsoUrl = (fullPath: string): string => {
    if (!fullPath || fullPath === '/') return '/';

    const [path, queryString] = fullPath.split('?');
    if (!queryString) return path;

    const params = new URLSearchParams(queryString);

    // Remove all known SSO noise
    SSO_PARAMETERS.forEach((param) => params.delete(param));

    const remainingQuery = params.toString();
    return remainingQuery ? `${path}?${remainingQuery}` : path;
};

const doSsoInit = (authService: AuthService, code: string): Observable<true> => {
    return authService.getSsoInit(code).pipe(
        startup(() => console.log('[SSOAuthGuard] Doing SSO init with code')),
        map((result: AuthenticationDto | RedirectDataDto) => {
            if ('url' in result) {
                throw new SsoRedirectError(result.url);
            }

            authService.headers = result.headers;
            authService.authentication = result;
            console.log('[SSOAuthGuard] SSO init done, got headers');
            return true;
        }),
    );
};

const redirectToSsoLogin = (authService: AuthService, redirectUri: string, dummyErrorFactory: () => unknown): Observable<GuardResult> => {
    authService.goToSsoLoginPage(redirectUri);

    return timer(1000).pipe(
        map(() => false),
        tap(() => throwError(dummyErrorFactory)),
    );
};

const isClientAuthenticated = (authService: AuthService): Observable<boolean> => {
    return authService.headerObs.pipe(
        first(),
        switchMap((headers) => {
            const authorizationHeader = headers?.find(({ key }) => key === 'Authorization');

            if (!authorizationHeader?.value?.startsWith('Bearer AT-')) {
                return of(false);
            }

            return of(true);
        }),
    );
};

const handleSsoError = (
    authService: AuthService,
    router: Router,
    sessionStorageKey: string,
    error: unknown,
    redirectPath: string,
): Observable<GuardResult> => {
    if (error instanceof SsoRedirectError) {
        console.log('[SSOAuthGuard] SSO requires logout – redirecting to error page');

        const redirectUri = `${window.location.origin}${redirectPath}`;
        const logoutUrl = appendQueryParam(error.logoutUrl, 'post_logout_redirect_uri', redirectUri);

        return of(
            router.createUrlTree(['error'], {
                queryParams: {
                    reason: ErrorPageReasons.SsoInit,
                    logoutUrl,
                    redirect: redirectPath,
                },
            }),
        );
    }

    const currentCounter = getSessionStorageInteger(sessionStorageKey);

    if (currentCounter >= MAX_SSO_INIT_TRIES) {
        console.log('[SSOAuthGuard] Too many SSO redirects - going to error page');

        const errorPageRedirect = redirectPath && redirectPath !== '/' ? redirectPath : undefined;

        return of(
            router.createUrlTree(['error'], {
                queryParams: {
                    reason: ErrorPageReasons.SsoInit,
                    redirect: errorPageRedirect,
                },
            }),
        );
    }

    console.log('[SSOAuthGuard] Redirecting to SSO login');

    setSessionStorageInteger(sessionStorageKey, currentCounter + 1);

    const redirectUri = `${window.location.origin}${redirectPath}`;
    return redirectToSsoLogin(authService, redirectUri, () => error);
};

const appendQueryParam = (url: string, key: string, value: string): string => {
    const urlObj = new URL(url);
    urlObj.searchParams.set(key, value);
    return urlObj.toString();
};

const ssoAuthGuard = (authService: AuthService, router: Router, rawPath: string): MaybeAsync<GuardResult> => {
    const sessionStorageKey = SSO_INIT_COUNTER_SESSION_STORAGE_COUNTER_KEY;
    const cleanPath = cleanSsoUrl(rawPath);

    return isClientAuthenticated(authService).pipe(
        first(),
        switchMap((isClientAuth) => {
            if (isClientAuth) {
                // If we are already authenticated but the URL is "dirty", redirect to clean URL
                if (rawPath !== cleanPath) {
                    return of(router.parseUrl(cleanPath));
                }
                return of(true);
            }

            // Not authenticated: try to handle the code
            const url = new URL(window.location.href);
            const code = url.searchParams.get('code');

            if (!code) {
                throw Error('No code in query');
            }

            return doSsoInit(authService, code).pipe(map(() => router.parseUrl(cleanPath)));
        }),
        catchError((error) => handleSsoError(authService, router, sessionStorageKey, error, cleanPath)),
        tap((result) => {
            // If result is true or a UrlTree, we consider it a success/progress
            if (result !== false) {
                resetSessionStorageInteger(sessionStorageKey);
            }
        }),
    );
};

export const ssoAuthCanActivateFn: CanActivateFn = (_, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    return ssoAuthGuard(authService, router, state.url);
};

export const ssoAuthCanMatchFn: CanMatchFn = () => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const redirectPath = router.getCurrentNavigation()?.extractedUrl.toString() || '/';
    return ssoAuthGuard(authService, router, redirectPath);
};
